import { api, API } from "./api";

const TOKEN_KEY = "wr_admin_token";

export function getAdminToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}
export function setAdminToken(t) {
  try { localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
}
export function clearAdminToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

const authHeaders = () => {
  // Legacy bearer only used as a fallback if a session cookie isn't present.
  // Endpoints that require auth accept EITHER a valid session cookie OR this header.
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Ensure every admin call carries cookies (session auth).
const withCreds = (opts = {}) => ({
  ...opts,
  withCredentials: true,
  headers: { ...(opts.headers || {}), ...authHeaders() },
});

// ---- Owner session (email + password) ----
export async function authLogin(email, password) {
  const { data } = await api.post("/auth/login", { email, password }, { withCredentials: true });
  return data;
}
export async function authLogout() {
  try { await api.post("/auth/logout", {}, { withCredentials: true }); } catch { /* ignore */ }
  clearAdminToken();
}
export async function authMe() {
  const { data } = await api.get("/auth/me", withCreds());
  return data;
}

// Public (used by public site — must not require auth)
export async function fetchListings(params = {}) {
  const { data } = await api.get("/listings", { params });
  return data;
}
export async function fetchListing(slug) {
  const { data } = await api.get(`/listings/${slug}`);
  return data;
}

// Admin auth (legacy bearer — retained for CI/CD scripts and preview fallback)
export async function adminLogin(token) {
  const { data } = await api.post("/admin/login", { token });
  return data;
}

// Admin — listings
export async function adminListListings(params = {}) {
  const { data } = await api.get("/admin/listings", withCreds({ params }));
  return data.listings;
}
export async function adminGetListing(id) {
  const { data } = await api.get(`/admin/listings/${id}`, withCreds());
  return data;
}
export async function adminPreviewListing(id) {
  const { data } = await api.get(`/admin/preview/${id}`, withCreds());
  return data;
}
export async function adminCreateDraft() {
  const { data } = await api.post("/admin/listings/draft", {}, withCreds());
  return data;
}
export async function adminCreateListing(payload) {
  const { data } = await api.post("/admin/listings", payload, withCreds());
  return data;
}
export async function adminUpdateListing(id, payload) {
  const { data } = await api.put(`/admin/listings/${id}`, payload, withCreds());
  return data;
}
export async function adminPatchListing(id, patch) {
  const { data } = await api.patch(`/admin/listings/${id}`, patch, withCreds());
  return data;
}
export async function adminDuplicateListing(id) {
  const { data } = await api.post(`/admin/listings/${id}/duplicate`, {}, withCreds());
  return data;
}

// Admin — dashboard
export async function adminFetchStats() {
  const { data } = await api.get("/admin/stats", withCreds());
  return data;
}
export async function adminFetchTaxonomy() {
  const { data } = await api.get("/admin/taxonomy", withCreds());
  return data;
}

// Admin — AI assist
export async function adminAiGenerate(payload) {
  const { data } = await api.post("/admin/ai/generate", payload, withCreds({ timeout: 60000 }));
  return data;
}

// Admin — upload
export async function adminUpload(file, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, withCreds({
    timeout: 120000,
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
    headers: { "Content-Type": "multipart/form-data" },
  }));
  return data;
}

// Admin — lifecycle
export async function adminTrashListing(id) {
  const { data } = await api.post(`/admin/listings/${id}/trash`, {}, withCreds());
  return data;
}
export async function adminRestoreListing(id) {
  const { data } = await api.post(`/admin/listings/${id}/restore`, {}, withCreds());
  return data;
}
export async function adminRevertLatest(id) {
  const { data } = await api.post(`/admin/listings/${id}/revert`, {}, withCreds());
  return data;
}
export async function adminListTrash() {
  const { data } = await api.get("/admin/trash", withCreds());
  return data.listings;
}
export async function adminHistory(id) {
  const { data } = await api.get(`/admin/listings/${id}/history`, withCreds());
  return data.entries;
}
export async function adminVersions(id) {
  const { data } = await api.get(`/admin/listings/${id}/versions`, withCreds());
  return data.versions;
}
export async function adminGlobalAudit(limit = 100) {
  const { data } = await api.get(`/admin/audit`, withCreds({ params: { limit } }));
  return data.entries;
}
export async function adminSweepOrphans() {
  const { data } = await api.post("/admin/cleanup/orphans", {}, withCreds());
  return data;
}

export async function adminDeleteListing(id) {
  const { data } = await api.delete(`/admin/listings/${id}`, withCreds());
  return data;
}

// Resolve a stored image path (e.g. "/api/files/wowromania/listings/xxx.jpg") to a full URL.
export function resolveImageUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}
